/**
 * Comprehension Question UI Helper Utilities
 *
 * Provides utility functions for working with COMPREHENSION questions using payload.blanks structure.
 * Works directly with npm-pikado-logic's raw API format (NO customChoices abstraction).
 */

/**
 * Blank item structure from npm-pikado-logic
 */
export interface BlankItem {
  name: string;          // e.g., "CHOICES1", "CHOICES2"
  choices: string[];     // Array of choice options
  correctAnswer: string; // The correct answer string (NOT an index)
}

/**
 * Cleans a string for AI processing by:
 * - Converting to lowercase
 * - Removing special characters and punctuation (except apostrophes)
 *
 * Used for non-COMPREHENSION question types (READ_SENTENCE, MISSING_WORD, etc.)
 */
export function cleanStringForAI(input: string): string {
  if (!input) return '';

  return input
    .toLowerCase()
    .replace(/[^a-z0-9'\s]/g, ''); // Keep letters, numbers, apostrophes, and spaces
}

/**
 * Detects CHOICES keywords (e.g., CHOICES1, CHOICES2) from text and textAI fields.
 * Returns unique CHOICES keywords found in both fields.
 *
 * Used when user types NEW text for COMPREHENSION questions to detect blanks.
 *
 * @param text - The question text field
 * @param textAI - The AI text field
 * @returns Array of unique CHOICES keywords (e.g., ["CHOICES1", "CHOICES2"])
 */
export function detectChoicesKeywords(text: string, textAI: string): string[] {
  const textWords = (text || '').split(' ');
  const aiTextWords = (textAI || '').split(' ');
  const allWords = [...textWords, ...aiTextWords];

  // Find all CHOICES keywords
  const choiceKeywords = allWords
    .filter((word) => word.startsWith('CHOICES'))
    .map((word) => word.replace(/[.,!?:;]+$/, '')); // Strip trailing punctuation

  // Remove duplicates using Set
  const uniqueKeywords = [...new Set(choiceKeywords)];

  return uniqueKeywords;
}

/**
 * Initializes empty blank items for detected CHOICES keywords.
 * Used when creating NEW COMPREHENSION questions.
 *
 * @param keywords - Array of CHOICES keywords detected from text
 * @returns Array of blank items with empty choices
 */
export function initializeEmptyBlanks(keywords: string[]): BlankItem[] {
  return keywords.map((keyword) => ({
    name: keyword,
    choices: ['', ''],  // Start with 2 empty choices
    correctAnswer: '',  // User will select correct answer
  }));
}

/**
 * Validates that a CHOICES keyword exists in text or textAI.
 * Used for form validation.
 *
 * @param text - The question text
 * @param textAI - The AI text
 * @returns True if at least one CHOICES keyword is found
 */
export function validateChoicesKeywordsExist(text: string, textAI: string): boolean {
  const keywords = detectChoicesKeywords(text, textAI);
  return keywords.length > 0;
}

/**
 * Replaces CHOICES keywords in text with the selected correct answer values.
 * Used in UI when user selects a correct answer to update the AI text.
 *
 * @param originalText - The original text containing CHOICES keywords
 * @param blanks - Array of blank items with correct answers
 * @returns Text with CHOICES keywords replaced by correct answer values
 */
export function replaceChoicesInText(
  originalText: string,
  blanks: BlankItem[]
): string {
  let resultText = originalText;

  blanks.forEach((blank) => {
    if (blank.correctAnswer) {
      const choiceKey = blank.name;

      // CLEANUP: Collapse any duplicate CHOICES keywords first
      resultText = resultText.replace(
        new RegExp(`\\b${choiceKey}(\\s+${choiceKey})*\\b`, 'g'),
        choiceKey
      );

      // Replace the choice keyword with the correct answer
      resultText = resultText.replace(
        new RegExp(`\\b${choiceKey}\\b`, 'g'),
        blank.correctAnswer
      );
    }
  });

  return resultText;
}

/**
 * Cleans up the question text by collapsing any duplicate CHOICES keywords.
 * Each CHOICES keyword appears only once, regardless of the word count in the correct answer.
 *
 * Used in UI when user selects a correct answer to update the display text.
 *
 * @param originalText - The original question text
 * @param blanks - Array of blank items with correct answers
 * @returns Updated text with single CHOICES keywords
 */
export function duplicateChoicesKeywordsByWordCount(
  originalText: string,
  blanks: BlankItem[]
): string {
  let normalText = originalText;

  blanks.forEach((blank) => {
    if (!blank.correctAnswer) return;

    const choiceKey = blank.name;

    // Clean up any existing duplicates - keep only single keyword
    normalText = normalText.replace(
      new RegExp(`\\b${choiceKey}(\\s+${choiceKey})*\\b`, 'g'),
      choiceKey
    );
  });

  return normalText;
}
